import { CopyCheck } from "lucide-react";
import { useState } from "react";
import { useNotify, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { MergePreview } from "./MergePreview";
import {
  duplicatesForContact,
  otherContactName,
  useDuplicates,
  type DuplicatePair,
} from "./useDuplicates";

/**
 * "Possible duplicate" banner on a contact record.
 *
 * Duplicates are surfaced where the problem is noticed rather than in a
 * separate module nobody visits. The banner is deliberately quiet, amber, not
 * red, because a name match is frequently two real people, and a scary
 * warning would push users into merging records that should stay separate.
 *
 * Renders nothing when the contact has no suspected duplicates, so it costs
 * no space on the overwhelming majority of records.
 */
export const DuplicateWarning = ({ contactId }: { contactId: number }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { data } = useDuplicates();
  const [reviewing, setReviewing] = useState<DuplicatePair | null>(null);

  const pairs = duplicatesForContact(data, contactId);
  if (pairs.length === 0) return null;

  const best = pairs[0];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
        <CopyCheck
          className="size-4 shrink-0 text-amber-700"
          strokeWidth={1.75}
        />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-amber-900">
            {pairs.length === 1
              ? "Possible duplicate"
              : `${pairs.length} possible duplicates`}
          </p>
          <p className="text-[12px] text-amber-800">
            {pairs.length === 1
              ? `Looks like ${otherContactName(best, contactId)}, ${best.reason.toLowerCase()}.`
              : `Including ${otherContactName(best, contactId)}, ${best.reason.toLowerCase()}.`}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {pairs.slice(0, 3).map((pair) => (
            <Button
              key={`${pair.contact_a}-${pair.contact_b}`}
              size="sm"
              variant="outline"
              className="h-8 border-amber-300 bg-white hover:bg-amber-100"
              onClick={() => setReviewing(pair)}
            >
              {pairs.length === 1
                ? "Review & merge"
                : `Review ${otherContactName(pair, contactId)}`}
            </Button>
          ))}
        </div>
      </div>

      <Dialog
        open={Boolean(reviewing)}
        onOpenChange={(open) => !open && setReviewing(null)}
      >
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review before merging</DialogTitle>
          </DialogHeader>
          {reviewing ? (
            <MergePreview
              pair={reviewing}
              onDone={(message) => {
                if (message) notify(message);
                setReviewing(null);
                refresh();
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
